import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { Loader2, IndianRupee, X, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import api from '../services/api';

const QRPaymentModal = ({ isOpen, onClose, bookingId, amountDue, onPaid }) => {
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState(null);
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | active | paid | expired | closed
  const [closing, setClosing] = useState(false);
  const [pollWarning, setPollWarning] = useState('');
  const pollFailCountRef = useRef(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const { toast } = useToast();

  const stopTimers = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const generateQR = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.request('/payments/qr-code/create', {
        method: 'POST',
        body: JSON.stringify({ booking_id: bookingId, amount: amountDue }),
      });
      setQr(data);
      setStatus(data.status || 'active');
      setSecondsLeft(data.expires_in_seconds || 300);
    } catch (e) {
      setError(e.message || 'Could not generate QR. Make sure Razorpay QR is enabled on the venue account.');
    } finally {
      setLoading(false);
    }
  }, [bookingId, amountDue]);

  // Auto-generate when modal opens
  useEffect(() => {
    if (isOpen && !qr) {
      generateQR();
    }
    if (!isOpen) {
      stopTimers();
      setQr(null);
      setStatus('idle');
      setError(null);
    }
  }, [isOpen, qr, generateQR]);

  // Countdown
  useEffect(() => {
    if (!qr || status !== 'active') return;
    tickRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          setStatus('expired');
          clearInterval(tickRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [qr, status]);

  // Status polling every 3s
  useEffect(() => {
    if (!qr || status !== 'active') return;
    pollFailCountRef.current = 0;
    setPollWarning('');
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.request(`/payments/qr-code/${qr.qr_id}/status`);
        pollFailCountRef.current = 0;
        if (pollWarning) setPollWarning('');
        if (s.status === 'paid') {
          setStatus('paid');
          stopTimers();
          toast({ title: 'Payment Received', description: `₹${amountDue} via UPI QR.` });
          if (typeof onPaid === 'function') onPaid(s);
        } else if (s.status === 'expired' || s.status === 'closed') {
          setStatus(s.status);
          stopTimers();
        }
      } catch (e) {
        // Soft fail — network blips during polling are common. Surface a
        // warning after 3 consecutive failures so users aren't left guessing.
        pollFailCountRef.current += 1;
        console.warn(`QR status poll failed (attempt ${pollFailCountRef.current}):`, e);
        if (pollFailCountRef.current >= 3) {
          setPollWarning("Can't reach payment server. We'll keep retrying — use 'Sync from Razorpay' if you're sure the customer paid.");
        }
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [qr, status, amountDue, onPaid, toast, pollWarning]);

  const handleClose = async () => {
    if (qr && status === 'active') {
      setClosing(true);
      try {
        await api.request(`/payments/qr-code/${qr.qr_id}/close`, { method: 'POST' });
      } catch (e) {
        // Best-effort close. The modal still closes locally; the QR will
        // auto-expire on Razorpay's side within 5 minutes.
        console.warn('Failed to close QR on server; will auto-expire.', e);
        toast({
          title: 'QR not closed cleanly',
          description: 'It will auto-expire shortly. No action needed.',
        });
      }
      setClosing(false);
    }
    stopTimers();
    onClose();
  };

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="p-0 overflow-hidden flex flex-col w-[96vw] max-w-[96vw] max-h-[92vh] sm:max-w-lg sm:w-auto" data-testid="qr-payment-modal">
        <DialogHeader className="p-4 pb-2 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <IndianRupee className="h-4 w-4 text-emerald-600" />
            Collect Payment via QR
          </DialogTitle>
        </DialogHeader>

        <div className="px-3 sm:px-5 pt-3 pb-2 space-y-3 overflow-y-auto flex-1 min-h-0">
          {loading && (
            <div className="flex flex-col items-center justify-center py-10" data-testid="qr-loading">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <p className="text-sm text-gray-500 mt-3">Generating QR…</p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="qr-error">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Could not generate QR</p>
                  <p className="text-xs mt-1">{error}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={generateQR} data-testid="qr-retry">
                <RefreshCw className="h-3.5 w-3.5" /> Retry
              </Button>
            </div>
          )}

          {qr && status === 'active' && !loading && (
            <div className="space-y-3" data-testid="qr-active-state">
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Amount Due</p>
                <p className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-1">
                  <IndianRupee className="h-5 w-5" />
                  {amountDue.toFixed(0)}
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-1 flex items-center justify-center">
                {qr.image_url ? (
                  <img src={qr.image_url} alt="UPI QR" className="w-full h-auto max-h-[55vh] object-contain" data-testid="qr-image" />
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center text-xs text-gray-400">QR not available</div>
                )}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                <p className="text-xs text-amber-800">
                  Expires in <strong className="font-mono" data-testid="qr-countdown">{timeStr}</strong> · Single use · Fixed amount
                </p>
              </div>
              {pollWarning && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center" data-testid="qr-poll-warning">
                  <p className="text-xs text-red-700">{pollWarning}</p>
                </div>
              )}
              <p className="text-center text-[11px] text-gray-500 px-1">
                Customer scans with any UPI app (GPay, PhonePe, Paytm). Booking auto-marks paid on success.
              </p>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api.request(`/payments/qr-code/${qr.qr_id}/sync`, { method: 'POST' });
                    if (r.status === 'paid') {
                      setStatus('paid');
                      stopTimers();
                      toast({ title: 'Payment Found', description: `₹${r.amount_paid} synced from Razorpay.` });
                      if (typeof onPaid === 'function') onPaid(r);
                    } else {
                      toast({ title: 'No payment yet', description: r.message || 'Razorpay shows no payment for this QR.' });
                    }
                  } catch (e) {
                    toast({ title: 'Sync failed', description: e.message, variant: 'destructive' });
                  }
                }}
                className="w-full text-xs text-blue-600 hover:underline flex items-center justify-center gap-1"
                data-testid="qr-manual-sync-btn"
              >
                <RefreshCw className="h-3 w-3" /> Already paid? Sync from Razorpay
              </button>
            </div>
          )}

          {status === 'paid' && (
            <div className="text-center py-6 space-y-3" data-testid="qr-paid-state">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <p className="text-lg font-semibold text-gray-900">Payment Received</p>
              <p className="text-sm text-gray-600">₹{amountDue.toFixed(0)} via UPI QR</p>
              <Button onClick={onClose} className="w-full" data-testid="qr-done-btn">Done</Button>
            </div>
          )}

          {(status === 'expired' || status === 'closed') && !loading && (
            <div className="text-center py-6 space-y-3" data-testid="qr-expired-state">
              <p className="text-base font-semibold text-gray-900">QR {status === 'expired' ? 'Expired' : 'Closed'}</p>
              <p className="text-sm text-gray-500">{status === 'expired' ? 'The 5-minute window has elapsed.' : 'QR was closed.'}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} className="flex-1" data-testid="qr-close-final">Close</Button>
                <Button onClick={generateQR} className="flex-1 gap-1.5" data-testid="qr-regenerate-btn">
                  <RefreshCw className="h-3.5 w-3.5" /> New QR
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer — keeps Cancel button always reachable on mobile */}
        {qr && status === 'active' && (
          <div className="border-t bg-white p-3 shrink-0" data-testid="qr-footer">
            <Button variant="outline" className="w-full gap-2" onClick={handleClose} disabled={closing} data-testid="qr-cancel-btn">
              {closing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              Cancel & Close QR
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QRPaymentModal;
